/**
 * 🪧 **The carved boards on the posts in front of the turf houses' doors.**
 *
 * 🎯 **haugur, 2026-09-04:** *"put a sign above the door that reprisents
 * storage, same goes for Búr, we need a sign that reprisents food storage… same
 * goes for smíðaverkstæði."* **D110.**
 *
 * ## 🔴 IT CAME OFF THE GABLE THE SAME DAY, AND THE REASON IS THE POINT
 *
 * 🎯 **haugur, with a photograph of his own row:** *"as you can see the signs
 * are still off from the building, maybe we should just design a sign that comes
 * on a little post up from the ground in front of each building."*
 *
 * ⚠️ **The board was wrong FOUR TIMES in one day and every fault was the same
 * fault: it was measured against the MODEL.** It had no place in the model's
 * load queue, so a building drawn before the board decoded never got one; it was
 * added to the pivot, which carries the model's scale, so 1.2 m came out five
 * times too big; the model's bounding box is its ROOF and the turf eave
 * overhangs the gable, so it floated a third of a metre proud; and the door
 * plane was measured before `updateMatrixWorld`.
 *
 * ✅ **A post stands on the GROUND at a fixed offset from the door plane** — a
 * number we chose, not one read off a photographed mesh. **Nothing about where
 * the sign goes has to know what the model looks like ever again.**
 *
 * ## 🔴 Why a sign exists at all
 *
 * The three buildings are **the same model**. A búr, a skemma and a smiðja are
 * told apart by nothing else, so **the board is not decoration — it is the only
 * thing that says which door you are standing at.** ⚠️ Treat it as a label, not
 * as trim: if it stops drawing, the game has three identical houses.
 *
 * ## Why PNG
 *
 * ⚠️ **The same split `itemicons.ts` records.** A block tile is uploaded to the
 * GPU as raw bytes, so `block-tiles.json` stays raw and this package stays
 * dependency-free. **A sign is decoded by the browser**, through `TextureLoader`
 * on a `data:` URI exactly as the tool photographs are, so **PNG needs no
 * decoder** and costs a quarter of the size.
 *
 * 👉 **Generated by `tools/gen-house-signs.mjs`** from `tools/house-sign-art.mjs`,
 * which the sample sheet haugur chose from reads too — one drawing, two callers,
 * so the record and the game cannot drift apart.
 */

import signData from '../data/house-signs.json' with { type: 'json' };

/** How wide the packed pictures are, in pixels. */
export const HOUSE_SIGN_WIDTH: number = signData.width;
/** How tall they are. */
export const HOUSE_SIGN_HEIGHT: number = signData.height;

const SIGNS: ReadonlyMap<string, string> = new Map(
  Object.entries(signData.icons).map(([key, b64]) => [key, `data:image/png;base64,${b64}`]),
);

/**
 * The board for one door block, as a data URI, or `undefined` for a block that
 * has no sign.
 *
 * ⚠️ **`undefined` is a real answer, not a fault** — it is what a door type
 * nobody has drawn a board for gets, and the gable is simply bare.
 */
export function houseSignUri(doorKey: string): string | undefined {
  return SIGNS.get(doorKey);
}

/** Every door key that has a board. ⚠️ For tests and tools. */
export function houseSignKeys(): readonly string[] {
  return [...SIGNS.keys()];
}

/**
 * 📏 **Where the board hangs, in metres, in the model's own frame.**
 *
 * 🔴 **The model is placed with its floor at y = 0 and centred on its
 * footprint**, and the gable carrying the door is the model's **−z** face
 * (`burmesh.ts`). So a board goes at x = 0, a little proud of that face.
 *
 * ⚠️ **The doorway is two metres tall**, so anything lower than that is on the
 * door itself; the gable's ridge is at 4.9 m, so anything higher is in the
 * turf. **2.9 m is the middle of what is left.**
 */
export const HOUSE_SIGN_PLACEMENT = {
  /**
   * How wide the board is, in metres.
   *
   * 🎯 **haugur chose 0.80 on 2026-09-04**, from four widths drawn at the size
   * each covers from six metres (`Design/samples/79`). ⚠️ **It was 1.2 on the
   * gable** — *"sign can be smaller that whaat we have in staging now."*
   */
  width: 0.8,
  /**
   * 🪧 **The middle of the board, above the floor.**
   *
   * ⚠️ **A player's eye is at about 1.6 m**, so this sits just under it and is
   * read straight ahead rather than by looking up at a gable.
   *
   * ⚠️ **It must clear the DOORWAY's height where it stands.** The doorway is
   * two metres and this board is beside it, not over it, so the constraint here
   * is the gable's own slope: at 1.35 m out from the middle of a 5 m wall there
   * is plenty of upright wall at this height.
   */
  centreY: 1.5,
  /**
   * 🔴 **How far proud of the WALL the board stands, in metres.**
   *
   * ⚠️ **This number was never the problem, and twice it was blamed.** 🎯
   * **haugur, 2026-09-04**, of a board six centimetres out: *"no they are
   * not"* flat. 🎯 **And again, 2026-09-07:** the boards *"stand out from the
   * wall."*
   *
   * 🔴 **Both times the standoff was tiny and the SURFACE it was measured from
   * was the wrong one.** On 2026-09-07 the board sat 1 cm proud of the door's
   * bounding box at −4.45 m while the gable it hangs on is at −4.04 m — **41
   * centimetres out, on a wall whose entire surface relief is 10.** ✅ **It is
   * measured from the gable inside the board's own rectangle now**
   * (`frontmostZInPatch`), so this number means what it says.
   *
   * ⚠️ **One centimetre is enough**, and it is measured from the frontmost
   * bump under the board, not from the average — so the board clears the rough
   * turf everywhere and still cannot z-fight with it.
   */
  proud: 0.01,
  /**
   * 🔴 **How far to the LEFT of the door the board hangs, in metres.**
   *
   * 🎯 **haugur, 2026-09-04:** *"can we put the signs on the wall on the left
   * side of each door."* **Left as a player walking up to the door sees it** —
   * which is `+x` here, because the board is turned to face out of the −z
   * gable. ⚠️ **Checked in a screenshot rather than reasoned about**, since a
   * sign on the wrong side is exactly the kind of thing a sign convention gets
   * wrong.
   *
   * 👉 **1.35 m clears the doorway with air to spare.** The doorway is
   * `DOOR_HALF_WIDTH × 5 m` — 0.7 m either side of centre — and the board is
   * 0.8 m wide, so anything past 1.1 m is clear of it. ⚠️ **And it must stay
   * inside the 5 m gable**: at 1.35 the board's far edge is 1.75 m out, well
   * short of the 2.5 m corner.
   *
   * ⚠️ **The same side every time**, so a burstabær row reads as a row of signs
   * rather than a scatter.
   */
  toTheSide: 1.35,
} as const;
